<template>
  <form
    class="simulation-creation-menu column"
    @submit.prevent="createSimulation()"
  >
    <input-text
      :value="simulationView.newSimulationSettings.title"
      :label="$t('labels.name')"
      :placeholder="namePlaceholder"
      :autofocus="true"
      :prevent-submit="true"
      @enter="descriptionField?.focus?.()"
      @update:value="setNewSimulationSetting('title', $event)"
    />
    <input-texteditor
      ref="descriptionField"
      :value="simulationView.newSimulationSettings.description"
      :label="$t('labels.description')"
      @update:value="setNewSimulationSetting('description', $event)"
    />

    <div class="pv-2">
      <radio-button
        :value="simulationType"
        data="impulse"
        :label="$t('labels.impulseSimulation')"
        @update:value="setNewSimulationSetting('simulationType', $event)"
      ></radio-button>
      <div
        v-if="simulationType == 'impulse'"
        class="simulation-creation-menu__radioafter"
      >
        {{ $t("messages.impulseSimulationDisclaimer") }}

        <details class="mt-2">
          <summary class="focus:outline outline-2 outline-action">
            {{ $t("labels.additionalOptions") }}
          </summary>
          <div class="column gap-2">
            <input-checkbox
              :value="simulationView.newSimulationSettings.actionsActive"
              class="mt-2"
              :label="$t('objectNames.action', 2)"
              @update:value="setNewSimulationSetting('actionsActive', $event)"
            />
            <input-checkbox
              :value="simulationView.newSimulationSettings.incidentsActive"
              :label="$t('objectNames.incident', 2)"
              @update:value="setNewSimulationSetting('incidentsActive', $event)"
            />
            <input-checkbox
              :value="simulationView.newSimulationSettings.momentumsActive"
              :label="$t('objectNames.momentum', 2)"
              @update:value="setNewSimulationSetting('momentumsActive', $event)"
            />
          </div>
          <input-number
            :value="roundAmount"
            :label="$t('labels.maxRounds')"
            :min="1"
            class="mt-2 mb-2"
            @update:value="setNewSimulationSetting('roundAmount', $event)"
          />
        </details>
      </div>

      <radio-button
        :value="simulationType"
        data="scenario"
        :label="$t('labels.scenarioSimulation')"
        @update:value="setNewSimulationSetting('simulationType', $event)"
      />

      <div
        v-if="simulationType == 'scenario'"
        class="simulation-creation-menu__radioafter"
      >
        <!-- SELECTING -->
        <input-dropdown
          v-if="scenariosSize > 0"
          :label="$t('objectNames.scenario')"
          :options="scenarios"
          :order="scenariosOrder"
          :value="chosenScenario"
          :must-confirm="true"
          @preselect="showScenarioInfo($event)"
          @update:value="selectScenario($event)"
        >
          <template #default="{ item }">
            {{ item.title }}
          </template>
        </input-dropdown>
      </div>
    </div>

    <button class="button" type="submit">
      {{ $t("labels.create") }}
    </button>
  </form>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { applyOrder } from "@/helpers/utility/Array";

import { useOverlay } from "@editor/ui/overlayDialog.store";
import { useSettingsStore } from "@editor/store/settings.store";
import { useScenarioStore } from "@editor/items/scenario/scenarios.store";
import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";

import InputDropdown from "@core/input/InputDropdown.vue";
import InputText from "@core/input/InputText.vue";
import InputTexteditor from "@core/input/TextEditor.vue";
import RadioButton from "@core/input/RadioButton.vue";
import InputCheckbox from "@core/input/InputCheckbox.vue";
import InputNumber from "@core/input/InputNumber.vue";

import { useSimulationCreation } from "./useSimulationCreation";

const simulationView = useSimulationViewStore();
const settingsStore = useSettingsStore();

const { t } = useI18n();

const descriptionField = ref<InstanceType<typeof InputText>>();

const { chosenScenario, simulationType, createSimulation } = useSimulationCreation();
  
const scenarios = computed(() => useScenarioStore().items);
const scenariosSize = computed(() => useScenarioStore().length);

const { setNewSimulationSetting } = simulationView;

const roundAmount = computed(() => {
  return (
    simulationView.newSimulationSettings.roundAmount ??
    settingsStore.simulationRoundAmount
  );
});

const scenariosOrder = computed(() => {
  return useScenarioStore().order;
});

const namePlaceholder = computed(() => {
  let name = "";
  if (simulationType.value === "scenario") {
    name += (chosenScenario.value?.title || "") + " ";
  }
  name += t(`labels.${simulationType.value}Simulation`);

  return name;
});


// update scenario id:
const scenarioId = simulationView.newSimulationSettings.scenarioId;
if (scenarioId == null || !scenarios.value[scenarioId]) {
  simulationView.setNewSimulationSetting(
    "scenarioId",
    applyOrder(scenarios.value, scenariosOrder.value)[0].id
  );
}

function selectScenario(scenario: { id: number }) {
  simulationView.setNewSimulationSetting(
    "scenarioId",
    scenario.id
  );
  hideScenarioInfo();
}

function showScenarioInfo(scenario: { type: "scenario"; id: number }) {
  useOverlay().setItem(scenario);
}

function hideScenarioInfo() {
  useOverlay().hide();
}
</script>

<style lang="scss">
.simulation-creation-menu {
  &__radioafter {
    font-size: 13px;
    padding-left: 2rem;
    padding-top: 0.25rem;
    padding-bottom: 0.5rem;
  }
}
</style>
../expression/expressionFunctions